import { Region } from '@experiences/constants';
import {
    Features,
    useFeatureFlagValue,
} from '@experiences/feature-flags';
import { OrgSelfServeMigrationEvent } from '@experiences/telemetry';
import {
    SpacingToken,
    UiSelect,
    UiStack,
    UiText,
} from '@experiences/ui-common';
import { useRegionDisplay } from '@experiences/util';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import { makeStyles } from '@mui/styles';
import { PortalAlertBar } from '@uipath/portal-shell-react';
import React, { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import useSWR from 'swr';

import { DataResidencyCloudLink } from '../../../../common/constants/documentation/DocumentationLinksDefault';
import { DataResidencyCloudLinkK8s } from '../../../../common/constants/documentation/DocumentationLinksK8s';
import { getOrganizationServiceIds } from '../../../../common/constants/ServicesMapping';
import { useDocumentationLinks } from '../../../../common/hooks/useDocumentationLink';
import useIsOnPrem from '../../../../common/hooks/useIsOnPrem';
import {
    getAvailableRegionsPerService,
    getOrganizationMigrationStatus,
} from '../../../../services/global-workflow-service/OrgMigration';
import {
    getOrganizationData,
    getSupportedRegions,
} from '../../../../services/organization/OrganizationService';
import {
    accountGlobalId,
    accountLogicalName,
} from '../../../../store/selectors';
import { useTelemetryHelper } from '../../../../telemetry/TelemetryHelper';
import {
    MigrationServiceAvailabilityHeader,
    PlatformServicesInfoCard,
    regionSelectStyles,
} from '../../../common/migration/migrationShared';
import type { IMigrationFormData } from '../../../common/migration/types';
import { UiMigrationServiceCard } from '../../../common/UiCard';
import UiCardGrid from '../../../common/UiCardGrid/UiCardGrid';

const useStyles = makeStyles(() =>
    ({
        ...regionSelectStyles(),
        alert: { paddingRight: '16px' },
    }),
);

export const OrgMigrationStepSelectRegion: React.FC = () => {
    const classes = useStyles();
    const { formatMessage: translate } = useIntl();
    const getLocalizedLinks = useDocumentationLinks();
    const getRegionDisplayValue = useRegionDisplay();

    const gwsMigrationSupportedRegions = useFeatureFlagValue(
        Features.GWSOrgMigrationSupportedRegions.name,
        Features.GWSOrgMigrationSupportedRegions.defaultValue
    ) as string[];

    const {
        watch,
        control,
        formState: { errors },
    } = useFormContext<IMigrationFormData>();
    const { logEvent } = useTelemetryHelper();
    const accountName = useSelector(accountLogicalName);
    const partitionGlobalId = useSelector(accountGlobalId);

    const isOnPrem = useIsOnPrem();

    const { data: organizationData } = useSWR({
        url: 'getOrganizationData',
        organizationGuid: partitionGlobalId,
    }, getOrganizationData);

    const { data: availableServices } = useSWR({
        organizationGuid: partitionGlobalId,
        accountName,
        url: 'getSupportedRegions',
    }, getSupportedRegions);

    const { data: availableRegions } = useSWR({
        accountName,
        url: 'getAvailableRegionsPerService',
    }, () => getAvailableRegionsPerService(partitionGlobalId));

    const { data: migrationStatus } = useSWR({
        accountName,
        url: 'getOrganizationMigrationStatusForRegion',
    }, () => getOrganizationMigrationStatus(partitionGlobalId));

    const sourceRegion = watch('sourceRegion');
    const destinationRegion = watch('destinationRegion');

    const {
        organizationLevelRegions, organizationServices,
    } = useMemo(() => {
        if (!availableServices) {
            return {
                organizationLevelRegions: [],
                organizationServices: [],
            };
        }

        const identityService = availableServices.find(service => service.id === 'identity');
        const filteredServices = availableServices.filter(service => getOrganizationServiceIds().includes(service.id));

        let regions = identityService?.supportedRegions ?? [];

        if (regions.length > 0) {
            regions = regions.filter(region =>
                region in Region && gwsMigrationSupportedRegions.includes(region as string)
            );
        }

        return {
            organizationLevelRegions: regions,
            organizationServices: filteredServices,
        };
    }, [ availableServices, gwsMigrationSupportedRegions ]);

    const currentServiceToRegionMap = useMemo(() => {
        const map: Record<string, string> = {};
        organizationData?.organizationServiceInstances.forEach(service => {
            map[service.serviceType] = service.region;
        });
        return map;
    }, [ organizationData ]);

    const allowedRegions = useMemo(
        () => {
            const filtered = (organizationLevelRegions ?? []).filter(x => x !== sourceRegion);
            const hasMisalignedServices = migrationStatus?.hasMisalignedServices ?? false;
            if (hasMisalignedServices && sourceRegion && !filtered.includes(sourceRegion)) {
                filtered.push(sourceRegion);
            }
            return filtered;
        },
        [ organizationLevelRegions, sourceRegion, migrationStatus ],
    );

    const regionOptions = useMemo(() =>
        Object.assign({}, ...(allowedRegions ?? []).map((x) =>
            ({ [Region[x]]: getRegionDisplayValue(x) }))),
    [ allowedRegions, getRegionDisplayValue ]);

    const supportedServicesInNewRegion = useMemo(() => {
        if (!availableRegions || destinationRegion === Region.None) {
            return [];
        }
        return (availableRegions.availableRegionsPerService[destinationRegion] || []).map(x => x.service);
    }, [ availableRegions, destinationRegion ]);

    return (
        <UiStack
            direction='column'
            gap={SpacingToken.XL}>
            <UiStack gap={SpacingToken.S}>
                <TextField
                    label={translate({ id: 'CLIENT_ORGANIZATION_MIGRATION_SOURCE_REGION' })}
                    value={getRegionDisplayValue(sourceRegion)}
                    variant="outlined"
                    data-cy="source-region"
                    className={classes.regionSource}
                />
                <ArrowRightAltIcon className={classes.arrowIcon} />
                <UiSelect
                    id="destinationRegion"
                    name="destinationRegion"
                    control={control}
                    isTranslated
                    inputLabel={translate({ id: 'CLIENT_ORGANIZATION_MIGRATION_DESTINATION_REGION' })}
                    options={regionOptions}
                    error={!!errors.destinationRegion}
                    required
                    className={classes.regionTarget}
                    dataCy="region-select-field"
                />
            </UiStack>

            <MigrationServiceAvailabilityHeader
                descriptionKey="CLIENT_ORGANIZATION_MIGRATION_REGION_AVAILABILITY"
                telemetryEvent={OrgSelfServeMigrationEvent.ReadMore}
                telemetryLocation="org-migration-select-region-1"
            />

            <PlatformServicesInfoCard />

            <UiCardGrid
                maxCardWidth='330px'
                style={{ maxWidth: 'unset' }}
                position='left'>
                {organizationServices.map((service, index) => (
                    <UiMigrationServiceCard
                        key={`org-service-card-${index}`}
                        region={currentServiceToRegionMap[service.id] ?? sourceRegion}
                        serviceId={service.id}
                        newRegion={destinationRegion}
                        supportedServicesInNewRegion={supportedServicesInNewRegion}
                    />
                ))}
            </UiCardGrid>

            <PortalAlertBar
                status='info'
                cancelable={false}>
                <UiStack
                    className={classes.alert}
                    direction='column'>
                    <UiText>
                        {translate({ id: 'CLIENT_ORGANIZATION_MIGRATION_ALERT_1' })}
                    </UiText>
                    <Link
                        onClick={() => logEvent(OrgSelfServeMigrationEvent.ReadMore, { Location: 'org-migration-select-region-2' })}
                        target="_blank"
                        href={getLocalizedLinks({ articleSlug: isOnPrem ? DataResidencyCloudLinkK8s : DataResidencyCloudLink })}>
                        {translate({ id: 'CLIENT_ORGANIZATION_MIGRATION_ALERT_2' })}
                    </Link>
                    <br />
                    <UiText>
                        {translate({ id: 'CLIENT_ORGANIZATION_MIGRATION_ALERT_3' })}
                    </UiText>
                </UiStack>
            </PortalAlertBar>

        </UiStack>
    );
};
